import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    CircularProgress,
    List,
    ListItemButton,
    ListItemText,
    TextField,
    Typography,
} from "@mui/material";
import { fetchQuestion, fetchQuestionsPaginated } from "../../../../utils/api";
import type { Question } from "../../../../types/rubric";
import { QuestionType } from "../../../../types/rubric";
import type { ResolverAttributeEntry, ResolverAttributeAnswerOption } from "../../../../types/resolver";

const DEBOUNCE_MS = 350;
const SEARCH_PAGE_SIZE = 20;

const makeAttributeId = () => `attr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/** questionLabel for resolver: use name (backend may expose label separately). */
const getQuestionLabel = (q: Question): string => (q as Question & { label?: string }).label ?? q.name ?? q.questionText ?? String(q.id);

function toAnswerOptions(answers: Question["answers"]): ResolverAttributeAnswerOption[] {
    if (!answers?.length) return [];
    return answers.map((a) => ({
        id: a.id ?? 0,
        displayText: a.displayText ?? a.name ?? a.tagLabel ?? String(a.id),
    }));
}

export interface QuestionSearchAttributeAdderProps {
    onAdd: (entry: ResolverAttributeEntry) => void;
    existingQuestionIds?: number[];
    disabled?: boolean;
    /** Optional label for the add button / section. */
    label?: string;
}

export const QuestionSearchAttributeAdder: React.FC<QuestionSearchAttributeAdderProps> = ({
    onAdd,
    existingQuestionIds = [],
    disabled = false,
}) => {
    const [search, setSearch] = useState("");
    const [searchDebounced, setSearchDebounced] = useState("");
    const [questions, setQuestions] = useState<Question[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const t = setTimeout(() => setSearchDebounced(search), DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        if (!searchDebounced.trim()) {
            setQuestions([]);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        fetchQuestionsPaginated({ search: searchDebounced.trim(), page: 0, sortBy: "name", sortDirection: "asc" })
            .then((res) => {
                if (!cancelled) {
                    setQuestions(res.content ?? []);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : "Failed to search questions");
                    setQuestions([]);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [searchDebounced]);

    const existingSet = useMemo(() => new Set(existingQuestionIds), [existingQuestionIds]);
    const filteredQuestions = useMemo(
        () => questions.filter((q) => !existingSet.has(q.id)),
        [questions, existingSet]
    );

    const addFromQuestion = useCallback(
        (q: Question) => {
            const questionLabel = getQuestionLabel(q);
            const baseEntry: ResolverAttributeEntry = {
                id: makeAttributeId(),
                questionId: q.id,
                questionLabel,
                answerSetId: q.answerSetId ?? undefined,
                answerSetName: q.answerSetName ?? undefined,
                answerOptions: toAnswerOptions(q.answers),
            };
            if (q.questionType === QuestionType.FREE_TEXT) {
                baseEntry.answerDisplayText = "";
                onAdd(baseEntry);
                setSearch("");
                setSearchDebounced("");
                setQuestions([]);
                return;
            }
            if (q.questionType === QuestionType.MULTIPLE_CHOICE) {
                const options = q.answers ?? [];
                const first = options[0];
                if (first?.id != null) {
                    baseEntry.answerValueId = first.id;
                    onAdd(baseEntry);
                    setSearch("");
                    setSearchDebounced("");
                    setQuestions([]);
                    return;
                }
                fetchQuestion(q.id).then((full) => {
                    const opts = toAnswerOptions(full.answers);
                    baseEntry.answerOptions = opts;
                    const firstAnswer = opts[0];
                    if (firstAnswer) {
                        baseEntry.answerValueId = firstAnswer.id;
                    }
                    onAdd(baseEntry);
                    setSearch("");
                    setSearchDebounced("");
                    setQuestions([]);
                }).catch((err) => console.error('Failed to fetch question details for attribute entry:', err));
            }
        },
        [onAdd]
    );

    const handleSelectQuestion = useCallback(
        (q: Question) => {
            if (q.questionType === QuestionType.MULTIPLE_CHOICE && (!q.answers || q.answers.length === 0)) {
                fetchQuestion(q.id)
                    .then((full) => addFromQuestion(full))
                    .catch((err) => console.error('Failed to fetch question for selection:', err));
            } else {
                addFromQuestion(q);
            }
        },
        [addFromQuestion]
    );

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <TextField
                size="small"
                placeholder="Search questions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={disabled}
                fullWidth
                inputProps={{ "data-testid": "question-search-input" }}
            />
            {loading && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">Searching...</Typography>
                </Box>
            )}
            {error && (
                <Typography variant="caption" color="error">
                    {error}
                </Typography>
            )}
            {searchDebounced.trim() && filteredQuestions.length > 0 && (
                <List dense sx={{ maxHeight: 200, overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
                    {filteredQuestions.map((q) => (
                        <ListItemButton
                            key={q.id}
                            onClick={() => handleSelectQuestion(q)}
                            data-testid={`question-result-${q.id}`}
                        >
                            <ListItemText
                                primary={q.name}
                                secondary={q.questionText ? (q.questionText.length > 60 ? `${q.questionText.slice(0, 60)}…` : q.questionText) : q.questionType}
                            />
                        </ListItemButton>
                    ))}
                </List>
            )}
            {searchDebounced.trim() && filteredQuestions.length === 0 && !loading && (
                <Typography variant="caption" color="text.secondary">
                    No questions found. Try a different search.
                </Typography>
            )}
        </Box>
    );
};
